/**
 * Calque d'instance — le cœur du modèle « UN exercice paramétré » (migration
 * 0229, correction de cadrage de 0221).
 *
 * Un Blueprint ne produit PAS N exercices : il produit UN exercice V2 dont les
 * blocs portent le rendu d'une instance CANONIQUE, et une BANQUE d'instances
 * (`blueprint_instances`). Chaque instance est un CALQUE indexé par uuid de
 * bloc : `{ "<uuid>": { content_md, prompt_md, answer_md, … } }`. La RPC
 * `get_exercise_v2_for_student` l'applique par un simple `data || calque`,
 * APRÈS la troncature freemium — donc le paywall, le scoring et le player
 * n'ont rien à savoir du Blueprint.
 *
 * ⚠️ INVARIANT FONDATEUR : la TOPOLOGIE est invariante par instance. Seuls les
 * TEXTES (et les valeurs numériques du mode calculatrice) varient. C'est ce qui
 * rend le scoring QCM inchangé : `submit_block_answer` juge
 * `p_chosen_block_id = p_correction_block_id` — une identité de bloc, pas une
 * valeur. Le nombre de questions et de distracteurs est fixé par la spec, pas
 * par le tirage (un tirage qui violerait une contrainte est rejeté et rejoué,
 * il ne produit jamais une forme différente).
 *
 * Pur — aucun accès réseau/DB.
 */

import { z } from 'zod';
import type { GeneratedInstance, GeneratedVisualBlock } from './generate-instance';

// ── Conventions de client_id ─────────────────────────────────────────────────
// Partagées entre le constructeur d'ops (apps/app, generated-instance-to-blocks)
// et la construction du block_map : c'est le `client_id_map` renvoyé par
// `exercise-blocks-mutate` qui fait le pont client_id → uuid réel.

export const BLUEPRINT_STATEMENT_CLIENT_ID = 'stmt';
export const blueprintQuestionClientId = (i: number): string => `q_${i}`;
export const blueprintCorrectionClientId = (i: number): string => `c_${i}`;
export const blueprintDistractorClientId = (i: number, j: number): string => `c_${i}_d${j}`;

// ── block_map (colonne exercise_blueprints.block_map) ───────────────────────

export const BlueprintQuestionBlockRefSchema = z.object({
  questionId: z.string().min(1),
  questionBlockId: z.string().uuid(),
  correctionBlockId: z.string().uuid(),
  distractorBlockIds: z.array(z.string().uuid()),
});
export type BlueprintQuestionBlockRef = z.infer<typeof BlueprintQuestionBlockRefSchema>;

export const BlueprintBlockMapSchema = z.object({
  statementBlockId: z.string().uuid(),
  questions: z.array(BlueprintQuestionBlockRefSchema).min(1),
});
export type BlueprintBlockMap = z.infer<typeof BlueprintBlockMapSchema>;

// ── Payload d'instance (colonne blueprint_instances.payload) ────────────────

export type BlueprintBlockOverlay = Record<string, unknown>;

export interface BlueprintInstancePayload {
  seed: string;
  parameters: Record<string, number>;
  results: Record<string, number>;
  /** Calque indexé par uuid de bloc — appliqué tel quel par la RPC (`data || calque`). */
  blocks: Record<string, BlueprintBlockOverlay>;
}

export class BlueprintOverlayError extends Error {}

export interface InstanceToPayloadOptions {
  /**
   * Conversion du visuel généré vers la forme runtime d'un bloc visuel V2.
   * Injectée depuis apps/app (`toExerciseVisualBlock`) : `packages/db` ne
   * dépend pas de `@jeraisonne/ai`. Absente ⇒ le visuel n'est pas calqué (le
   * rendu canonique figé dans `data.visuals` reste affiché).
   */
  renderVisual?: (visual: GeneratedVisualBlock) => unknown;
}

/**
 * Construit le calque d'une instance à partir de la carte des blocs de
 * l'exercice paramétrique. Lève si la topologie de l'instance ne correspond
 * plus à celle de l'exercice matérialisé (spec modifiée sans re-matérialiser)
 * — mieux vaut refuser d'écrire une instance bancale que servir un exercice
 * dont les distracteurs ne correspondent plus aux blocs.
 */
export function instanceToPayload(
  instance: GeneratedInstance,
  blockMap: BlueprintBlockMap,
  opts: InstanceToPayloadOptions = {},
): BlueprintInstancePayload {
  if (blockMap.questions.length !== instance.questions.length) {
    throw new BlueprintOverlayError(
      `Topologie divergente : ${blockMap.questions.length} question(s) dans l'exercice, ${instance.questions.length} dans l'instance. Re-matérialise l'exercice paramétrique.`,
    );
  }

  const blocks: Record<string, BlueprintBlockOverlay> = {};

  blocks[blockMap.statementBlockId] = {
    content_md: instance.statement_md,
    ...(instance.visual && opts.renderVisual
      ? { visuals: [opts.renderVisual(instance.visual)] }
      : {}),
  };

  instance.questions.forEach((question, i) => {
    const ref = blockMap.questions[i]!;
    if (ref.questionId !== question.questionId) {
      throw new BlueprintOverlayError(
        `Topologie divergente : question #${i} est "${ref.questionId}" dans l'exercice, "${question.questionId}" dans l'instance. Re-matérialise l'exercice paramétrique.`,
      );
    }

    blocks[ref.questionBlockId] = { prompt_md: question.prompt_md };

    if (question.answerMode === 'calculator') {
      const answer_md = question.unitLabel
        ? `${formatValue(question.expectedValue)} ${question.unitLabel}`
        : formatValue(question.expectedValue);
      blocks[ref.correctionBlockId] = {
        answer_md,
        explanation_md: question.explanation_md,
        // Relu SERVEUR par submit_block_answer_numeric (seul juge) ; envoyé
        // aussi au client, qui en a besoin pour afficher le pavé de
        // composition — posture anti-triche identique au reste du V2.
        expected_value: question.expectedValue,
        available_variables: question.availableVariables,
      };
      if (ref.distractorBlockIds.length > 0) {
        throw new BlueprintOverlayError(
          `Topologie divergente : la question "${question.questionId}" est passée en mode calculatrice mais l'exercice porte encore ${ref.distractorBlockIds.length} distracteur(s).`,
        );
      }
      return;
    }

    if (ref.distractorBlockIds.length !== question.distractors_md.length) {
      throw new BlueprintOverlayError(
        `Topologie divergente : question "${question.questionId}" — ${ref.distractorBlockIds.length} distracteur(s) dans l'exercice, ${question.distractors_md.length} dans l'instance. Re-matérialise l'exercice paramétrique.`,
      );
    }

    blocks[ref.correctionBlockId] = {
      answer_md: question.correctText_md,
      explanation_md: question.explanation_md,
    };
    question.distractors_md.forEach((label_md, j) => {
      blocks[ref.distractorBlockIds[j]!] = { label_md };
    });
  });

  return {
    seed: instance.seed,
    parameters: instance.parameterValues,
    results: instance.results,
    blocks,
  };
}

/** Même rendu numérique que le moteur (cf. formatNumber) — dupliqué ici pour ne pas créer de cycle d'import. */
function formatValue(value: number): string {
  return String(Math.round(value * 1e6) / 1e6);
}

/**
 * Construit le `block_map` depuis le `client_id_map` renvoyé par
 * `exercise-blocks-mutate` après l'écriture des blocs canoniques.
 */
export function blueprintBlockMapFromClientIdMap(
  instance: GeneratedInstance,
  clientIdMap: Record<string, string>,
): BlueprintBlockMap {
  const need = (clientId: string): string => {
    const uuid = clientIdMap[clientId];
    if (!uuid) {
      throw new BlueprintOverlayError(
        `client_id "${clientId}" absent du client_id_map renvoyé par exercise-blocks-mutate`,
      );
    }
    return uuid;
  };

  return {
    statementBlockId: need(BLUEPRINT_STATEMENT_CLIENT_ID),
    questions: instance.questions.map((question, i) => ({
      questionId: question.questionId,
      questionBlockId: need(blueprintQuestionClientId(i)),
      correctionBlockId: need(blueprintCorrectionClientId(i)),
      distractorBlockIds:
        question.answerMode === 'qcm'
          ? question.distractors_md.map((_d, j) => need(blueprintDistractorClientId(i, j)))
          : [],
    })),
  };
}
